import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiActivity, FiAlertCircle, FiArrowLeft, FiBriefcase, FiCheck,
  FiChevronRight, FiEdit2, FiFilter, FiLoader, FiMail, FiPlus,
  FiRefreshCw, FiSearch, FiSlash, FiUsers, FiX
} from 'react-icons/fi';
import { useClient } from '../context/ClientContext';
import { vendorService } from '../services/vendorService';

const INPUT = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:bg-slate-50';
const LABEL = 'mb-1.5 block text-xs font-semibold text-slate-600';
const CARD = 'rounded-2xl border border-slate-200 bg-white shadow-sm';
const TERMS = [
  ['due_on_receipt', 'Due on receipt'], ['net_15', 'Net 15'],
  ['net_30', 'Net 30'], ['net_60', 'Net 60']
];
const CURRENCIES = ['USD', 'PHP', 'EUR', 'GBP', 'CAD', 'AUD', 'SGD', 'JPY', 'AED', 'INR'];
const blankVendor = () => ({
  vendor_name: '', company_name: '', email: '', phone: '', website: '',
  billing_address_line1: '', billing_address_line2: '', billing_city: '',
  billing_state: '', billing_postal_code: '', billing_country: '',
  payment_terms: 'net_30', currency: 'USD', tax_id: '', notes: '', is_active: true
});

function Field({ label, required, children, className = '' }) {
  return <div className={className}><label className={LABEL}>{label}{required && <span className="ml-1 text-rose-500">*</span>}</label>{children}</div>;
}

function Section({ title, subtitle, children }) {
  return (
    <section className={`${CARD} p-5 sm:p-6`}>
      <div className="mb-5 border-b border-slate-100 pb-4">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function Stat({ icon: Icon, title, value, tone }) {
  return (
    <div className={`${CARD} flex items-center gap-4 p-4 sm:p-5`}>
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon size={19} /></div>
      <div><p className="text-xs text-slate-500">{title}</p><p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{value}</p></div>
    </div>
  );
}


function VendorStatusModal({
  vendor,
  loading,
  onCancel,
  onConfirm
}) {
  useEffect(() => {
    if (!vendor) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !loading) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [vendor, loading, onCancel]);

  if (!vendor) return null;

  const isDeactivating = vendor.is_active;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-[3px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) {
          onCancel();
        }
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="vendor-status-title"
        aria-describedby="vendor-status-description"
        className="w-full max-w-[440px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      >
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                isDeactivating
                  ? 'bg-amber-50 text-amber-600'
                  : 'bg-emerald-50 text-emerald-600'
              }`}
            >
              {isDeactivating ? (
                <FiAlertCircle size={23} />
              ) : (
                <FiCheck size={23} />
              )}
            </div>

            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              aria-label="Close confirmation"
              className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            >
              <FiX size={18} />
            </button>
          </div>

          <h2
            id="vendor-status-title"
            className="mt-5 text-xl font-semibold tracking-tight text-slate-900"
          >
            {isDeactivating
              ? 'Deactivate this vendor?'
              : 'Reactivate this vendor?'}
          </h2>

          <p
            id="vendor-status-description"
            className="mt-2 text-sm leading-6 text-slate-500"
          >
            {isDeactivating
              ? 'This vendor will be marked as inactive. Their information and existing accounting records will be preserved.'
              : 'This vendor will be marked as active again and can be selected for future purchases.'}
          </p>

          <div className="mt-5 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-sm font-semibold text-blue-600 shadow-sm">
              {vendor.vendor_name?.charAt(0)?.toUpperCase() || 'V'}
            </div>

            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {vendor.vendor_name}
              </p>

              <p className="mt-0.5 truncate text-xs text-slate-500">
                {vendor.company_name || vendor.email || 'Vendor record'}
              </p>
            </div>
          </div>

          {isDeactivating && (
            <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-amber-50 p-3.5 text-xs leading-5 text-amber-800">
              <FiAlertCircle className="mt-0.5 shrink-0" size={15} />
              <span>
                Deactivation does not delete this vendor or their
                transaction history.
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`inline-flex min-w-[155px] items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
              isDeactivating
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <FiLoader className="animate-spin" />
            ) : isDeactivating ? (
              <FiSlash />
            ) : (
              <FiCheck />
            )}

            {loading
              ? 'Updating...'
              : isDeactivating
                ? 'Deactivate vendor'
                : 'Reactivate vendor'}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function Vendors() {
  const { activeClient } = useClient();
  const organizationId = activeClient?.id;
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(blankVendor());
  const [saving, setSaving] = useState(false);
  const [changingId, setChangingId] = useState(null);
  const [confirmVendor, setConfirmVendor] = useState(null);

  const loadVendors = useCallback(async () => {
    if (!organizationId) { setVendors([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const rows = await vendorService.getVendors(organizationId);
      setVendors(rows || []);
    } catch (e) {
      setError(e.message || 'Unable to load vendors.');
    } finally { setLoading(false); }
  }, [organizationId]);

  useEffect(() => {
    // Clear data immediately when switching businesses.
    setVendors([]);
    setConfirmVendor(null);
    setFormOpen(false);
    setEditing(null);
    setNotice('');
    setError('');
    let cancelled = false;
    if (!organizationId) { setLoading(false); return undefined; }
    setLoading(true);
    vendorService.getVendors(organizationId)
      .then(rows => { if (!cancelled) setVendors(rows || []); })
      .catch(e => { if (!cancelled) setError(e.message || 'Unable to load vendors.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [organizationId]);

  const filtered = useMemo(() => vendors.filter(v => {
    if (statusFilter === 'active' && !v.is_active) return false;
    if (statusFilter === 'inactive' && v.is_active) return false;
    const term = search.trim().toLowerCase();
    return !term || [v.vendor_name, v.company_name, v.email, v.phone]
      .some(value => String(value || '').toLowerCase().includes(term));
  }), [vendors, statusFilter, search]);

  const stats = useMemo(() => ({
    all: vendors.length,
    active: vendors.filter(v => v.is_active).length,
    inactive: vendors.filter(v => !v.is_active).length
  }), [vendors]);

  const openForm = (vendor = null) => {
    setEditing(vendor?.id || null);
    setForm(vendor ? Object.fromEntries(Object.keys(blankVendor()).map(key => [key, vendor[key] ?? blankVendor()[key]])) : blankVendor());
    setError('');
    setNotice('');
    setFormOpen(true);
  };

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    if (saving || !organizationId) return;
    if (!form.vendor_name.trim()) { setError('Vendor name is required.'); return; }
    if (!/^[A-Z]{3}$/.test(form.currency)) { setError('Choose a valid currency.'); return; }
    setSaving(true);
    setError('');
    try {
      if (editing) await vendorService.updateVendor(organizationId, editing, form);
      else await vendorService.createVendor(organizationId, form);
      setFormOpen(false);
      setNotice(editing ? 'Vendor updated successfully.' : 'Vendor created successfully.');
      await loadVendors();
    } catch (e) {
      setError(e.message || 'Unable to save vendor.');
    } finally { setSaving(false); }
  };


  const toggleActive = (vendor) => {
    if (changingId || !organizationId) return;

    setConfirmVendor(vendor);
  };

  const closeStatusModal = useCallback(() => {
    if (changingId) return;
    setConfirmVendor(null);
  }, [changingId]);

  
  const confirmStatusChange = async () => {
    if (!confirmVendor || changingId || !organizationId) return;

    const vendor = confirmVendor;
    const nextStatus = !vendor.is_active;

    setChangingId(vendor.id);
    setError('');
    setNotice('');

    try {
      await vendorService.setVendorActive(
        organizationId,
        vendor.id,
        nextStatus
      );

      setVendors((previous) =>
        previous.map((item) =>
          item.id === vendor.id
            ? { ...item, is_active: nextStatus }
            : item
        )
      );

      setNotice(
        `Vendor ${nextStatus ? 'reactivated' : 'deactivated'} successfully.`
      );

      setConfirmVendor(null);
    } catch (e) {
      console.error('Failed to update vendor status:', e);

      setConfirmVendor(null);
      setError(e.message || 'Unable to update vendor status.');
    } finally {
      setChangingId(null);
    }
  };



  const textInput = (key, placeholder, type = 'text', props = {}) => (
    <input type={type} value={form[key] || ''} onChange={e => updateField(key, e.target.value)}
      placeholder={placeholder} className={INPUT} disabled={saving} {...props} />
  );

  if (!organizationId) return (
    <div className="flex min-h-[55vh] flex-col items-center justify-center gap-3 text-center">
      <FiBriefcase className="text-3xl text-slate-300" />
      <h2 className="text-base font-semibold text-slate-900">Select a business</h2>
      <p className="text-sm text-slate-500">Choose a client organization to manage its vendors.</p>
    </div>
  );

  return (
    <div className="mx-auto max-w-[1440px] space-y-6 pb-12">
      {formOpen ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <button type="button" onClick={() => setFormOpen(false)} disabled={saving} className="mb-3 inline-flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-900"><FiArrowLeft /> Back to vendors</button>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{editing ? 'Edit vendor' : 'New vendor'}</h1>
              <p className="mt-1 text-sm text-slate-500">{editing ? 'Update supplier information.' : 'Add a supplier or service provider to your business.'}</p>
            </div>
            <button type="button" onClick={() => setFormOpen(false)} disabled={saving} className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-500 hover:bg-slate-50" aria-label="Close form"><FiX /></button>
          </div>
          {error && <div role="alert" className="flex gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"><FiAlertCircle className="mt-0.5 shrink-0" />{error}</div>}
          <form onSubmit={save} className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="space-y-5">
              <Section title="Vendor details" subtitle="Basic information about the supplier.">
                <Field label="Vendor name" required className="sm:col-span-2">{textInput('vendor_name', 'e.g. Acme Office Supplies', 'text', { required: true, maxLength: 200 })}</Field>
                <Field label="Company name">{textInput('company_name', 'Registered company name (optional)')}</Field>
                <Field label="Website">{textInput('website', 'https://example.com', 'url')}</Field>
                <Field label="Email">{textInput('email', 'billing@example.com', 'email')}</Field>
                <Field label="Phone">{textInput('phone', '+1 (555) 000-0000', 'tel')}</Field>
              </Section>
              <Section title="Billing address" subtitle="Used for vendor records and future bills.">
                <Field label="Address line 1" className="sm:col-span-2">{textInput('billing_address_line1', 'Street address')}</Field>
                <Field label="Address line 2" className="sm:col-span-2">{textInput('billing_address_line2', 'Suite, unit, building (optional)')}</Field>
                <Field label="City">{textInput('billing_city', 'City')}</Field>
                <Field label="State / Province">{textInput('billing_state', 'State or province')}</Field>
                <Field label="Postal code">{textInput('billing_postal_code', 'ZIP or postal code')}</Field>
                <Field label="Country">{textInput('billing_country', 'Country')}</Field>
              </Section>
              <Section title="Payment & tax details" subtitle="Default values for future bills.">
                <Field label="Payment terms">
                  <select className={INPUT} value={form.payment_terms} onChange={e => updateField('payment_terms', e.target.value)} disabled={saving}>
                    {TERMS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </Field>
                <Field label="Currency">
                  <select className={INPUT} value={form.currency} onChange={e => updateField('currency', e.target.value)} disabled={saving}>
                    {CURRENCIES.map(currency => <option key={currency} value={currency}>{currency}</option>)}
                  </select>
                </Field>
                <Field label="Tax ID" className="sm:col-span-2">{textInput('tax_id', 'Optional tax registration number')}</Field>
                <Field label="Internal notes" className="sm:col-span-2">
                  <textarea rows={4} value={form.notes || ''} onChange={e => updateField('notes', e.target.value)} disabled={saving} placeholder="Internal notes about this vendor..." className={INPUT} />
                </Field>
              </Section>
            </div>
            <aside className="space-y-4 lg:sticky lg:top-6">
              <div className={`${CARD} p-5`}>
                <h3 className="text-sm font-semibold text-slate-900">Vendor status</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">Inactive vendors remain in your records but can be excluded from new transactions.</p>
                <label className="mt-4 flex cursor-pointer items-center justify-between rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-700">
                  Active vendor
                  <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={e => updateField('is_active', e.target.checked)}
                      disabled={saving || Boolean(editing)}
                      className="h-4 w-4 accent-blue-600"
                    />
                </label>
              </div>
              <div className={`${CARD} space-y-2 p-5`}>
                <button type="submit" disabled={saving} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
                  {saving ? <FiLoader className="animate-spin" /> : <FiCheck />}{saving ? 'Saving...' : editing ? 'Save changes' : 'Create vendor'}
                </button>
                <button type="button" onClick={() => setFormOpen(false)} disabled={saving} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              </div>
            </aside>
          </form>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Vendors</h1>
              <p className="mt-1 text-sm text-slate-500">Manage your suppliers, payment terms, and contact information.</p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={loadVendors} disabled={loading} title="Refresh vendors" className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-slate-600 hover:bg-slate-50 disabled:opacity-50"><FiRefreshCw className={loading ? 'animate-spin' : ''} /></button>
              <button type="button" onClick={() => openForm()} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"><FiPlus /> Add vendor</button>
            </div>
          </div>
          {notice && <div role="status" className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700"><FiCheck />{notice}<button type="button" className="ml-auto" onClick={() => setNotice('')} aria-label="Dismiss"><FiX /></button></div>}
          {error && <div role="alert" className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"><FiAlertCircle />{error}</div>}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat icon={FiUsers} title="Total vendors" value={stats.all} tone="bg-blue-50 text-blue-600" />
            <Stat icon={FiActivity} title="Active vendors" value={stats.active} tone="bg-emerald-50 text-emerald-600" />
            <Stat icon={FiSlash} title="Inactive vendors" value={stats.inactive} tone="bg-slate-100 text-slate-600" />
          </div>
          <div className={`${CARD} overflow-hidden`}>
            <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
              <div className="relative w-full sm:max-w-md">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors, companies, or contacts..." className={`${INPUT} pl-10`} aria-label="Search vendors" />
              </div>
              <div className="flex items-center gap-2"><FiFilter className="text-slate-400" /><select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`${INPUT} min-w-[145px]`} aria-label="Filter vendors by status"><option value="active">Active</option><option value="inactive">Inactive</option><option value="all">All vendors</option></select></div>
            </div>
            {loading ? (
              <div className="flex min-h-[260px] items-center justify-center gap-2 text-sm text-slate-500"><FiLoader className="animate-spin text-blue-600" /> Loading vendors...</div>
            ) : filtered.length === 0 ? (
              <div className="flex min-h-[300px] flex-col items-center justify-center px-5 text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500"><FiBriefcase size={22} /></div>
                <h3 className="text-base font-semibold text-slate-900">{vendors.length ? 'No matching vendors' : 'No vendors yet'}</h3>
                <p className="mt-1 max-w-sm text-sm text-slate-500">{vendors.length ? 'Try a different search or status filter.' : 'Add your first supplier to start organizing purchases and bills.'}</p>
                {!vendors.length && <button type="button" onClick={() => openForm()} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"><FiPlus /> Add first vendor</button>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-left">
                  <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500"><tr><th className="px-5 py-3.5">Vendor</th><th className="px-4 py-3.5">Contact</th><th className="px-4 py-3.5">Payment terms</th><th className="px-4 py-3.5">Currency</th><th className="px-4 py-3.5">Status</th><th className="px-5 py-3.5 text-right">Actions</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map(v => (
                      <tr key={v.id} className="hover:bg-slate-50/70">
                        <td className="px-5 py-4"><button type="button" onClick={() => openForm(v)} className="flex items-center gap-3 text-left"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-sm font-semibold text-blue-700">{v.vendor_name?.charAt(0)?.toUpperCase() || 'V'}</span><span><span className="block text-sm font-semibold text-slate-900 hover:text-blue-600">{v.vendor_name}</span>{v.company_name && <span className="mt-0.5 block text-xs text-slate-500">{v.company_name}</span>}</span></button></td>
                        <td className="px-4 py-4"><div className="space-y-1 text-xs text-slate-600">{v.email ? <a href={`mailto:${v.email}`} className="flex items-center gap-1.5 hover:text-blue-600"><FiMail className="text-slate-400" />{v.email}</a> : <span className="text-slate-400">No email</span>}{v.phone && <div>{v.phone}</div>}</div></td>
                        <td className="px-4 py-4 text-sm text-slate-600">{TERMS.find(([key]) => key === v.payment_terms)?.[1] || v.payment_terms}</td>
                        <td className="px-4 py-4 text-sm font-medium text-slate-600">{v.currency}</td>
                        <td className="px-4 py-4"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${v.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{v.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td className="px-5 py-4"><div className="flex items-center justify-end gap-1"><button type="button" onClick={() => openForm(v)} title="Edit vendor" aria-label={`Edit ${v.vendor_name}`} className="rounded-lg p-2 text-slate-500 hover:bg-blue-50 hover:text-blue-600"><FiEdit2 /></button><button type="button" disabled={changingId === v.id} onClick={() => toggleActive(v)} title={v.is_active ? 'Deactivate vendor' : 'Reactivate vendor'} aria-label={`${v.is_active ? 'Deactivate' : 'Reactivate'} ${v.vendor_name}`} className="rounded-lg px-2 py-2 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-50">{changingId === v.id ? <FiLoader className="animate-spin" /> : v.is_active ? 'Deactivate' : 'Activate'}</button></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && filtered.length > 0 && <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">Showing {filtered.length} of {vendors.length} vendors</div>}
          </div>
        </>
      )}
      
      <VendorStatusModal
        vendor={confirmVendor}
        loading={Boolean(changingId)}
        onCancel={closeStatusModal}
        onConfirm={confirmStatusChange}
      />

    </div>
  );
}